import { formatNumber, formatPercent } from "./helpers";

const getNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const HEALTH_STATUS_NOTE =
  "Health thresholds: Healthy >= 90% completed and < 5% exceptions; Watch >= 75% completed and < 15% exceptions; otherwise Needs Review.";

export const formatInsightPercent = (value) => formatPercent(getNumber(value));

const getScopeName = (row = {}) =>
  row.scopeName || row.agentName || row.serviceName || row.name || row.branchName || "Unknown";

const getTicketTotal = (row = {}) =>
  getNumber(row.total ?? row.totalTickets ?? row.totalAgentTickets ?? row.totalServiceData);

const getCompleted = (row = {}) =>
  getNumber(row.completed ?? row.numCompleted ?? row.totalCompleted);

const getNoShow = (row = {}) => getNumber(row.noShow ?? row.numNoShow ?? row.totalNoShow);

const getUnattended = (row = {}) =>
  getNumber(row.unattended ?? row.numUnattended ?? row.totalUnattended);

const getAverage = (row = {}) =>
  getNumber(
    row.average ??
      row.avg ??
      row.averageTicketsPerDay ??
      row.averageTicketsPerHour ??
      row.avgWaitingTimePerTicket
  );

const getMinimum = (row = {}) =>
  getNumber(row.min ?? row.minTicketsPerDay ?? row.minTicketsPerHour ?? row.minWaitingTimePerDay);

const getMaximum = (row = {}) =>
  getNumber(row.max ?? row.maxTicketsPerDay ?? row.maxTicketsPerHour ?? row.maxWaitingTimePerDay);

const getSeries = (row = {}) => {
  if (Array.isArray(row.days)) return row.days;
  if (Array.isArray(row.hours)) return row.hours;
  if (Array.isArray(row.chart)) return row.chart;
  return [];
};

const getPointTotal = (point = {}) =>
  getNumber(point.total ?? point.rate ?? point.count ?? point.value);

const getPointLabel = (point = {}) => point.day ?? point.hour ?? point.date ?? point.name ?? null;

const getPeakPoint = (series = []) =>
  series.reduce(
    (peak, point) => {
      const total = getPointTotal(point);
      if (total > peak.peakTickets) {
        return { peakPeriod: getPointLabel(point), peakTickets: total };
      }
      return peak;
    },
    { peakPeriod: null, peakTickets: 0 }
  );

export const flattenTicketRows = (input = []) => {
  const rows = Array.isArray(input) ? input : [input];

  return rows.flatMap((row) => {
    if (!row) return [];
    if (Array.isArray(row.serviceData)) {
      return row.serviceData.map((service) => ({ ...service, branchName: row.branchName }));
    }
    if (Array.isArray(row.servicesData)) {
      return row.servicesData.flatMap((service) => {
        if (Array.isArray(service.agentsData)) {
          return service.agentsData.map((agent) => ({
            ...agent,
            branchName: row.branchName,
            serviceName: service.serviceName || service.name,
          }));
        }
        return { ...service, branchName: row.branchName };
      });
    }
    if (Array.isArray(row.agentsData)) {
      return row.agentsData.map((agent) => ({ ...agent, branchName: row.branchName }));
    }
    return row;
  });
};

export const buildTicketVolumeInsights = (input = [], options = {}) => {
  const scopeLabel = options.scopeLabel || "Scope";
  const rows = flattenTicketRows(input).map((row) => {
    const total = getTicketTotal(row);
    const completed = getCompleted(row);
    const noShow = getNoShow(row);
    const unattended = getUnattended(row);
    const series = getSeries(row);
    const periodTotals = series.map(getPointTotal);
    const activePeriods = row.activePeriods ?? periodTotals.filter((value) => value > 0).length;
    const peak = row.peakPeriod !== undefined ? row : getPeakPoint(series);
    const exceptionTickets = row.exceptionTickets ?? noShow + unattended;
    const exceptionRate = row.exceptionRate ?? (total ? (exceptionTickets / total) * 100 : 0);
    const completionRate =
      row.completionRate ?? (total ? (completed / total) * 100 : getNumber(row.pctCompleted));
    return {
      ...row,
      scopeName: getScopeName(row),
      total,
      completed,
      noShow,
      unattended,
      average: getAverage(row),
      min: getMinimum(row),
      max: getMaximum(row),
      activePeriods,
      zeroTicketPeriods: row.zeroTicketPeriods ?? Math.max(periodTotals.length - activePeriods, 0),
      peakPeriod: peak.peakPeriod,
      peakTickets: getNumber(peak.peakTickets),
      exceptionTickets,
      exceptionRate,
      completionHealth: row.completionHealth || "Unrated",
      completedPct: completionRate,
      noShowPct: row.noShowRate ?? (total ? (noShow / total) * 100 : getNumber(row.pctNoShow)),
      unattendedPct:
        row.unattendedRate ?? (total ? (unattended / total) * 100 : getNumber(row.pctUnattended)),
    };
  });
  const activeRows = rows.filter((row) => row.total > 0);
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  const completed = rows.reduce((sum, row) => sum + row.completed, 0);
  const noShow = rows.reduce((sum, row) => sum + row.noShow, 0);
  const unattended = rows.reduce((sum, row) => sum + row.unattended, 0);
  const rankedRows = [...rows]
    .sort((left, right) => right.total - left.total || getScopeName(left).localeCompare(getScopeName(right)))
    .map((row, index) => ({
      ...row,
      rank: row.rank || index + 1,
      ticketSharePct: row.ticketSharePct ?? (total ? (row.total / total) * 100 : 0),
    }))
    .slice(0, options.limit || 10);
  const hasRankComparison = rankedRows.length > 1;
  const topRow = rankedRows[0];
  const highestNoShow = [...activeRows].sort((left, right) => right.noShowPct - left.noShowPct)[0];
  const highestUnattended = [...activeRows].sort(
    (left, right) => right.unattendedPct - left.unattendedPct
  )[0];

  return {
    kpis: [
      { label: "Total Tickets", value: formatNumber(total) },
      { label: "Completed", value: formatNumber(completed), note: formatInsightPercent(total ? (completed / total) * 100 : 0) },
      { label: "No Show", value: formatNumber(noShow), note: formatInsightPercent(total ? (noShow / total) * 100 : 0) },
      { label: "Unattended", value: formatNumber(unattended), note: formatInsightPercent(total ? (unattended / total) * 100 : 0) },
      ...(hasRankComparison
        ? [{ label: "Top Volume", value: topRow?.scopeName || "N/A", note: topRow ? formatNumber(topRow.total) : "" }]
        : []),
    ],
    tables: hasRankComparison ? [
      {
        title: `${scopeLabel} Ranking`,
        description: `Ranks ${scopeLabel.toLowerCase()}s by total ticket volume and shows completion, exception, peak, zero-ticket business periods, and health signals.`,
        columns: [
          { id: "scopeName", label: scopeLabel },
          { id: "total", label: "Total" },
          { id: "ticketSharePct", label: "Share" },
          { id: "completed", label: "Completed" },
          { id: "completedPct", label: "Completed %" },
          { id: "exceptionRate", label: "Exception %" },
          { id: "peakTickets", label: "Peak" },
          { id: "zeroTicketPeriods", label: "Zero Ticket Periods" },
          { id: "completionHealth", label: "Completion Health" },
        ],
        rows: rankedRows,
        renderCell: (row, columnId) => {
          if (columnId === "ticketSharePct" || columnId === "completedPct" || columnId === "exceptionRate") {
            return formatInsightPercent(row[columnId]);
          }
          if (columnId === "peakTickets") return `${formatNumber(row.peakTickets)} (${row.peakPeriod || "-"})`;
          if (typeof row[columnId] === "number") return formatNumber(row[columnId]);
          return row[columnId];
        },
      },
    ] : [],
    notes: hasRankComparison ? [
      HEALTH_STATUS_NOTE,
      highestNoShow
        ? `Highest no-show rate: ${highestNoShow.scopeName} (${formatInsightPercent(highestNoShow.noShowPct)}).`
        : null,
      highestUnattended
        ? `Highest unattended rate: ${highestUnattended.scopeName} (${formatInsightPercent(highestUnattended.unattendedPct)}).`
        : null,
    ].filter(Boolean) : [],
  };
};

const getDurationTotal = (row = {}, metric) =>
  getNumber(
    metric === "waiting"
      ? row.waitingTimeInSeconds ?? row.totalWaitingTime
      : row.sessionInSeconds ?? row.totalServiceTime
  );

const getDurationAverage = (row = {}, metric) =>
  getNumber(
    metric === "waiting"
      ? row.avg ?? row.average ?? row.avgWaitingTimePerTicket
      : row.avg ?? row.average
  );

export const buildDurationInsights = (input = [], options = {}) => {
  const metric = options.metric || "service";
  const scopeLabel = options.scopeLabel || "Service";
  const durationLabel = metric === "waiting" ? "Waiting Time" : "Service Time";
  const rows = flattenTicketRows(input).map((row) => {
    const completed = getCompleted(row);
    const totalDuration = getDurationTotal(row, metric);
    const average = getDurationAverage(row, metric);
    return {
      ...row,
      scopeName: getScopeName(row),
      completed,
      totalDuration,
      average,
      min: getMinimum(row),
      max: getMaximum(row),
      rankByServiceTime: row.rankByServiceTime ?? row.rankByWaitingTime,
      serviceTimeSharePct: getNumber(row.serviceTimeSharePct ?? row.waitingTimeSharePct),
      activePeriods: getNumber(row.activePeriods),
      zeroCompletedPeriods: getNumber(row.zeroCompletedPeriods),
      peakPeriod: row.peakPeriod,
      peakAverageServiceTime: getNumber(
        row.peakAverageServiceTime ?? row.peakAverageWaitingTime
      ),
      durationBand: row.durationBand || "N/A",
    };
  });
  const activeRows = rows.filter((row) => row.completed > 0 || row.totalDuration > 0);
  const totalCompleted = rows.reduce((sum, row) => sum + row.completed, 0);
  const totalDuration = rows.reduce((sum, row) => sum + row.totalDuration, 0);
  const weightedAverage = totalCompleted ? totalDuration / totalCompleted : 0;
  const rankedRows = [...rows]
    .sort(
      (left, right) =>
        right.totalDuration - left.totalDuration ||
        right.average - left.average ||
        getScopeName(left).localeCompare(getScopeName(right))
    )
    .slice(0, options.limit || 10);
  const hasRankComparison = rankedRows.length > 1;
  const slowestRows = [...activeRows].sort((left, right) => right.average - left.average).slice(0, 5);

  return {
    kpis: [
      { label: "Completed Tickets", value: formatNumber(totalCompleted) },
      { label: `Total ${durationLabel}`, value: options.formatDuration?.(totalDuration) || totalDuration },
      { label: "Avg / Ticket", value: options.formatDuration?.(weightedAverage) || weightedAverage },
      ...(hasRankComparison
        ? [
            { label: `Highest ${durationLabel}`, value: rankedRows[0]?.scopeName || "N/A" },
            { label: "Slowest Avg", value: slowestRows[0]?.scopeName || "N/A" },
          ]
        : []),
    ],
    tables: hasRankComparison ? [
      {
        title: `${scopeLabel} ${durationLabel} Ranking`,
        description: `Ranks ${scopeLabel.toLowerCase()}s by total ${durationLabel.toLowerCase()} and shows ticket count, average per ticket, peak average, zero-completion business periods, and banding.`,
        columns: [
          { id: "scopeName", label: scopeLabel },
          { id: "completed", label: "Tickets" },
          { id: "totalDuration", label: `Total ${durationLabel}` },
          { id: "serviceTimeSharePct", label: "Share" },
          { id: "average", label: "Avg / Ticket" },
          { id: "peakAverageServiceTime", label: "Peak Avg" },
          { id: "zeroCompletedPeriods", label: "Zero Completion Periods" },
          { id: "durationBand", label: "Band" },
          { id: "max", label: "Max" },
        ],
        rows: rankedRows,
        renderCell: (row, columnId) => {
          if (columnId === "serviceTimeSharePct") return formatInsightPercent(row[columnId]);
          if (columnId === "peakAverageServiceTime") {
            const formatted = options.formatDuration?.(row[columnId]) || row[columnId];
            return `${formatted} (${row.peakPeriod || "-"})`;
          }
          if (["totalDuration", "average", "max"].includes(columnId)) {
            return options.formatDuration?.(row[columnId]) || row[columnId];
          }
          if (typeof row[columnId] === "number") return formatNumber(row[columnId]);
          return row[columnId];
        },
      },
    ] : [],
    notes: hasRankComparison && slowestRows.length
      ? [`Slowest average ${durationLabel.toLowerCase()}: ${slowestRows[0].scopeName}.`]
      : [],
  };
};

const getChartPoints = (row = {}) => row.chart || row.charts || row.days || row.hours || [];

export const buildArrivalInsights = (input = [], options = {}) => {
  const scopeLabel = options.scopeLabel || "Scope";
  const rows = (Array.isArray(input) ? input : [input]).map((row) => {
    const chart = getChartPoints(row);
    const peak = [...chart].sort((left, right) => getNumber(right.rate ?? right.total) - getNumber(left.rate ?? left.total))[0];
    const activePeriods = getNumber(row.activeHours ?? row.activeDays);
    const zeroArrivalPeriods = getNumber(
      row.zeroArrivalBusinessHours ?? row.zeroArrivalBusinessDays
    );
    const averagePerPeriod = getNumber(
      row.averagePerBusinessHour ?? row.averagePerBusinessDay ?? row.average
    );
    const averageOnActivePeriods = getNumber(
      row.averageOnActiveHours ?? row.averageOnActiveDays ?? row.average
    );
    return {
      ...row,
      scopeName: getScopeName(row),
      total: getNumber(row.total),
      average: getAverage(row),
      min: getMinimum(row),
      max: getMaximum(row),
      activePeriods,
      zeroArrivalPeriods,
      averagePerPeriod,
      averageOnActivePeriods,
      peakLabel: row.peakHour ?? row.peakDay ?? peak?.date ?? peak?.hour ?? "N/A",
      peakValue: getNumber(row.peakValue ?? peak?.rate ?? peak?.total ?? row.max),
      range: getMaximum(row) - getMinimum(row),
      volatilityBand: row.volatilityBand || "N/A",
    };
  });
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  const rankedRows = [...rows]
    .sort((left, right) => right.total - left.total || getScopeName(left).localeCompare(getScopeName(right)))
    .slice(0, options.limit || 10);
  const hasRankComparison = rankedRows.length > 1;

  return {
    kpis: [
      { label: "Total Visitors", value: formatNumber(total) },
      { label: "Peak Period", value: rankedRows[0]?.peakLabel || "N/A", note: rankedRows[0] ? formatNumber(rankedRows[0].peakValue) : "" },
      { label: "Zero Arrival Periods", value: formatNumber(rows.reduce((sum, row) => sum + row.zeroArrivalPeriods, 0)) },
      { label: "Volatility", value: rankedRows[0]?.volatilityBand || "N/A" },
      ...(hasRankComparison
        ? [{ label: "Top Demand", value: rankedRows[0]?.scopeName || "N/A", note: rankedRows[0] ? formatNumber(rankedRows[0].total) : "" }]
        : []),
    ],
    tables: hasRankComparison ? [
      {
        title: `${scopeLabel} Demand Ranking`,
        description: `Ranks ${scopeLabel.toLowerCase()}s by total arrivals and shows average demand, active periods, zero-arrival business periods, peaks, and volatility.`,
        columns: [
          { id: "scopeName", label: scopeLabel },
          { id: "total", label: "Total" },
          { id: "average", label: "Average" },
          { id: "averagePerPeriod", label: "Avg / Period" },
          { id: "activePeriods", label: "Active Periods" },
          { id: "zeroArrivalPeriods", label: "Zero Arrival Periods" },
          { id: "peakLabel", label: "Peak" },
          { id: "peakValue", label: "Peak Value" },
          { id: "volatilityBand", label: "Volatility" },
        ],
        rows: rankedRows,
        renderCell: (row, columnId) =>
          typeof row[columnId] === "number" ? formatNumber(row[columnId]) : row[columnId],
      },
    ] : [],
    notes: hasRankComparison && rankedRows[0] ? [`Highest demand: ${rankedRows[0].scopeName}.`] : [],
  };
};
